import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";

import { faCommentExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useProfileContext } from "contexts/ProfileContext";
import { useSavedInputsContext } from "contexts/SavedInputsContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { TextArea } from "shared/atoms/inputs/TextArea";
import { Spinner } from "shared/atoms/Spinner";
import { museoSansFont } from "theme/muiTheme";
import { CurrentPatientContext } from "utils/contexts";

import { StickyNoteContainer } from "./PatientViewStickyNote";
import { putPatientNote } from "./Queries/putPatientNote";

interface PatientEditStickyNoteProps {
  readonly setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setStickyNoteMessage: React.Dispatch<React.SetStateAction<string>>;
  readonly stickyNoteMessage: string;
}

export const PatientEditStickyNote: React.FC<PatientEditStickyNoteProps> = ({
  setIsEditing,
  setStickyNoteMessage,
  stickyNoteMessage,
}) => {
  const { t } = useTranslation();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { patient } = useContext(CurrentPatientContext);
  const { profile } = useProfileContext();
  const { savedStickyNotes, setSavedStickyNotes } = useSavedInputsContext();
  const patientsStickyNote = savedStickyNotes[patient.id] ?? stickyNoteMessage;

  invariant(profile);

  useEffect(() => {
    return () => {
      if (textAreaRef.current) {
        setSavedStickyNotes({ [patient.id]: textAreaRef.current.value });
      }
    };
  }, []);

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.value = patientsStickyNote;
    }
  }, [textAreaRef]);

  const onSave = async () => {
    setIsSaving(true);
    const note = textAreaRef?.current?.value ?? "";
    await putPatientNote({ id: patient.id, therapistId: profile.id, note });
    setStickyNoteMessage(note);
    setSavedStickyNotes({ [patient.id]: note });
    setIsEditing(false);
  };

  if (isSaving) {
    return (
      <LoadingContainer>
        <Spinner dataTestId="spinner" small />
      </LoadingContainer>
    );
  }

  return (
    <StickyEditNoteContainer data-testid="edit-patient-sticky-note">
      <Container>
        <FontAwesomeIcon icon={faCommentExclamation} style={{ color: "#307FE2" }} />
        <StyledTextArea placeholder={t("sticky_note.default")} ref={textAreaRef} rows={3} />
      </Container>
      <Stack direction={{ xs: "column-reverse", sm: "row" }} py={1.5} gap={2}>
        <BaseButton
          variant="secondary"
          onClick={() => setIsEditing(false)}
          data-testid="cancel-sticky-note"
          text={t("buttons.cancel")}
        />
        <BaseButton variant="primary" onClick={onSave} data-testid="save-sticky-note" text={t("buttons.save")} />
      </Stack>
    </StickyEditNoteContainer>
  );
};

const LoadingContainer = styled(StickyNoteContainer)`
  background-color: ${props => props.theme.colors.white};
  justify-content: center;
`;

const StyledTextArea = styled(TextArea)`
  width: 90%;
  textarea {
    font-family: ${museoSansFont};
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.S_5};
  * {
    margin-right: ${props => props.theme.spacing.S_15};
  }
`;

const StickyEditNoteContainer = styled(StickyNoteContainer)`
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
`;
